import axios from "axios";
import API_URLS from "../../../config/global_variables";

const authToken = () => {
  try {
    return JSON.parse(localStorage.userData || "{}").token || "";
  } catch {
    return "";
  }
};

const headers = () => ({
  "Content-Type": "application/json",
  Authorization: authToken(),
});

const FRIENDLY = {
  VALIDATION_ERROR: "Please fix the highlighted fields.",
  NOT_FOUND: "Product type not found — it may have been deleted in another tab.",
  DUPLICATE_KEY: "A product type with this name already exists for this store.",
  SERVICE_UNAVAILABLE: "Service is temporarily unavailable. Please try again.",
  SERVER_ERROR: "Something went wrong. Please try again.",
  NETWORK_ERROR: "Network error. Check your connection and try again.",
  CANCELLED: "",
};

const mapError = (err) => {
  if (axios.isCancel?.(err) || err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
    return { code: "CANCELLED", status: 0, message: "", fieldErrors: [] };
  }
  // Backend envelope: { success:false, error:{ code, message, details:[{ path, message }] } }
  const apiErr = err?.response?.data?.error || {};
  const status = err?.response?.status;
  const code =
    apiErr.code ||
    (status === 409
      ? "DUPLICATE_KEY"
      : status === 404
      ? "NOT_FOUND"
      : status === 400
      ? "VALIDATION_ERROR"
      : status >= 500
      ? "SERVER_ERROR"
      : status
      ? `HTTP_${status}`
      : "NETWORK_ERROR");

  const fieldErrors = (Array.isArray(apiErr.details) ? apiErr.details : [])
    .filter((d) => d && (d.path || d.field))
    .map((d) => ({
      field: String(d.path || d.field),
      message: String(d.message || "Invalid value"),
    }));

  let message =
    FRIENDLY[code] ||
    apiErr.message ||
    (status ? `Request failed (${status})` : err?.message) ||
    FRIENDLY.SERVER_ERROR;
  if (code === "VALIDATION_ERROR" && fieldErrors.length) {
    message = fieldErrors.map((f) => `${f.field}: ${f.message}`).join("\n");
  }
  return { code, status, message, fieldErrors };
};

const buildListParams = (filters = {}) => {
  const params = {};
  if (filters.type_name) params.type_name = filters.type_name;
  if (filters.store_hash) params.store_hash = filters.store_hash;
  params.page = filters.page || 1;
  params.limit = filters.limit || 10;
  return params;
};

const fetchProductTypes = async (filters, { signal } = {}) => {
  const res = await axios.get(API_URLS.PRODUCT_TYPE, {
    headers: headers(),
    params: buildListParams(filters),
    signal,
  });
  // Backend envelope: { success:true, data:{ items:[...], pagination:{...} } }
  const payload = res?.data?.data || {};
  return {
    items: Array.isArray(payload.items) ? payload.items : [],
    pagination: payload.pagination || {
      page: filters.page || 1,
      limit: filters.limit || 10,
      total: 0,
      totalPages: 0,
    },
  };
};

const fetchProductTypeById = async (id, { signal } = {}) => {
  const res = await axios.get(API_URLS.PRODUCT_TYPE_BY_ID(id), {
    headers: headers(),
    signal,
  });
  return res?.data?.data || null;
};

const createProductType = async (payload) => {
  const res = await axios.post(API_URLS.PRODUCT_TYPE, payload, {
    headers: headers(),
  });
  return res?.data?.data || null;
};

const updateProductType = async (id, patch) => {
  const res = await axios.put(API_URLS.PRODUCT_TYPE_BY_ID(id), patch, {
    headers: headers(),
  });
  return res?.data?.data || null;
};

const deleteProductType = async (id) => {
  const res = await axios.delete(API_URLS.PRODUCT_TYPE_BY_ID(id), {
    headers: headers(),
  });
  return res?.data?.data || null;
};

const apiHelpers = {
  fetchProductTypes,
  fetchProductTypeById,
  createProductType,
  updateProductType,
  deleteProductType,
  mapError,
};

export default apiHelpers;
